import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateOrganization, useOrganizations } from '../api/queries'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import { isValidOrganizationCodeFormat, isValidOrganizationCodeLength, suggestOrganizationCode } from '../lib/organization-code'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { JurisdictionSelect } from './JurisdictionSelect'
import { apiClient } from '../api/client'

/** The organization just created, and whether it follows a template. */
export interface CreatedOrganization {
  code: string
  fromTemplate: boolean
}

interface CreateOrganizationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Called once the organization exists. One created from a template has no
   * types yet: the caller leads the admin to `/{code}/types/sync`, where the
   * first sync brings them over (spec 2026-09-25-02).
   */
  onSuccess?: (created: CreatedOrganization) => void
}

export function CreateOrganizationDialog({ open, onOpenChange, onSuccess }: CreateOrganizationDialogProps) {
  const { t } = useTranslation('types')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  // Once the admin edits the code, the name stops overwriting it (#1528).
  const [codeEdited, setCodeEdited] = useState(false)
  const [isTemplate, setIsTemplate] = useState(false)
  const [templateOrganizationId, setTemplateOrganizationId] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string>('')
  const [codeError, setCodeError] = useState<string>('')
  const [jurisdiction, setJurisdiction] = useState('')
  const [jurisdictionError, setJurisdictionError] = useState('')

  // Fetch template organizations
  const { data: templateOrgs = [] } = useOrganizations({ is_template: true })

  const createOrganization = useCreateOrganization({
    onSuccess: (org) => {
      const created: CreatedOrganization = { code: org.code ?? code, fromTemplate: !!templateOrganizationId }
      // Reset form
      setCode('')
      setCodeEdited(false)
      setName('')
      setIsTemplate(false)
      setTemplateOrganizationId(undefined)
      setJurisdiction('')
      setError('')

      onSuccess?.(created)
    },
    onError: (err: any) => {
      setError(err?.title || err?.message || t('create_org.create_failed'))
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setCodeError('')
    setJurisdictionError('')

    if (!isValidOrganizationCodeFormat(code)) {
      setCodeError(t('create_org.code_invalid'))
      return
    }

    if (!isValidOrganizationCodeLength(code)) {
      setCodeError(t('create_org.code_length'))
      return
    }

    if (!name.trim()) {
      setError(t('create_org.name_required'))
      return
    }

    if (!jurisdiction) {
      setJurisdictionError(t('create_org.jurisdiction_required'))
      return
    }

    createOrganization.mutate({
      code,
      name,
      is_template: isTemplate,
      template_organization_id: templateOrganizationId,
      jurisdiction,
    })
  }

  // A template's jurisdiction is the natural default for an organization
  // created from it; the admin can still change it.
  const chooseTemplate = async (uid: string | undefined) => {
    setTemplateOrganizationId(uid)
    const tpl = templateOrgs.find((o) => o.uid === uid)
    if (!tpl?.code) return
    const { data } = await apiClient.GET('/api/v1/organizations/{orgCode}', { params: { path: { orgCode: tpl.code } } })
    if (data?.jurisdiction) setJurisdiction(data.jurisdiction)
  }

  const handleClose = () => {
    if (!createOrganization.isPending) {
      setCode('')
      setCodeEdited(false)
      setName('')
      setIsTemplate(false)
      setTemplateOrganizationId(undefined)
      setJurisdiction('')
      setJurisdictionError('')
      setError('')
      setCodeError('')
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-organization-dialog">
        <DialogHeader>
          <DialogTitle>{t('create_org.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Organization Name */}
          <div className="space-y-2">
            <Label htmlFor="org-name">
              {t('create_org.name_label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (!codeEdited) setCode(suggestOrganizationCode(e.target.value))
              }}
              placeholder="ACME Corporation"
              disabled={createOrganization.isPending}
              data-testid="org-name-input"
              required
              autoFocus
            />
          </div>

          {/* Organization Code */}
          <div className="space-y-2">
            <Label htmlFor="org-code">
              {t('create_org.code_label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="org-code"
              value={code}
              onChange={(e) => {
                const next = e.target.value.toLowerCase()
                setCode(next)
                // Clearing the code hands it back to the name.
                setCodeEdited(next !== '')
              }}
              placeholder={t('create_org.code_placeholder')}
              disabled={createOrganization.isPending}
              data-testid="org-code-input"
              required
            />
            <p className="text-xs text-muted-foreground">
              {t('create_org.code_hint')}
            </p>
            {codeError && (
              <p className="text-sm text-destructive" data-testid="org-code-error">{codeError}</p>
            )}
          </div>

          {/* Template Organization */}
          <div className="space-y-2">
            <Label htmlFor="template-org">{t('create_org.template_label')}</Label>
            <Select
              value={templateOrganizationId || "none"}
              onValueChange={(value) => void chooseTemplate(value === "none" ? undefined : value)}
              disabled={createOrganization.isPending}
            >
              <SelectTrigger id="template-org" data-testid="template-org-select">
                <SelectValue placeholder={t('create_org.template_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('create_org.template_none')}</SelectItem>
                {templateOrgs.map((org) => (
                  <SelectItem key={org.uid} value={org.uid}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground" data-testid="template-org-hint">
              {t('sync.create_org_hint')}
            </p>
          </div>

          {/* Jurisdiction */}
          <div className="space-y-2">
            <Label>
              {t('create_org.jurisdiction_label')} <span className="text-destructive">*</span>
            </Label>
            <JurisdictionSelect
              value={jurisdiction}
              onValueChange={(v) => {
                setJurisdiction(v)
                setJurisdictionError('')
              }}
              includeAll={false}
              disabled={createOrganization.isPending}
              placeholder={t('create_org.jurisdiction_placeholder')}
              data-testid="org-jurisdiction-select"
            />
            <p className="text-xs text-muted-foreground">{t('create_org.jurisdiction_hint')}</p>
            {jurisdictionError && (
              <p className="text-sm text-destructive" data-testid="org-jurisdiction-error">{jurisdictionError}</p>
            )}
          </div>

          {/* Is Template */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-template"
              checked={isTemplate}
              onCheckedChange={(checked) => setIsTemplate(checked === true)}
              disabled={createOrganization.isPending}
              data-testid="is-template-checkbox"
            />
            <Label htmlFor="is-template" className="cursor-pointer">
              {t('create_org.is_template')}
            </Label>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createOrganization.isPending}
              data-testid="cancel-button"
            >
              {t('create_org.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createOrganization.isPending || !code || !name}
              data-testid="create-button"
            >
              {createOrganization.isPending ? t('create_org.creating') : t('create_org.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}